import type { PermissionCatalogGroup } from "../types/permissions";

export interface PermissionItemDefinition {
  code: string;
  label: string;
  hint?: string;
}

export interface PermissionGroupDefinition {
  title: string;
  description?: string;
  permissions: PermissionItemDefinition[];
}

export interface PermissionGroupView {
  title: string;
  description?: string;
  items: PermissionItemDefinition[];
}

const GROUP_DEFINITIONS: PermissionGroupDefinition[] = [
  {
    title: "Menü Erişimleri",
    permissions: [
      { code: "Dashboard.View", label: "Dashboard menüsünü görüntüle" },
      { code: "Content.View", label: "İçerik menüsünü görüntüle" },
      { code: "Users.View", label: "Kullanıcılar menüsünü görüntüle" },
      { code: "Comments.View", label: "Yorumlar menüsünü görüntüle" },
      { code: "Economy.View", label: "Ekonomi menüsünü görüntüle" },
      { code: "ZTokens.View", label: "Z-Token menüsünü görüntüle" },
      { code: "Revenue.View", label: "Gelir raporunu görüntüle" },
      { code: "Finance.View", label: "Finance menüsünü görüntüle" },
      { code: "Gamification.View", label: "Gamification menüsünü görüntüle" },
      { code: "Permissions.View", label: "Roller/İzinler menüsünü görüntüle" },
      { code: "Notifications.View", label: "Bildirim menüsünü görüntüle" },
      { code: "Logs.View", label: "Log ekranını görüntüle" },
    ],
  },
  {
    title: "İçerik İşlemleri",
    permissions: [
      { code: "Content.Create", label: "İçerik oluştur" },
      { code: "Content.EditAll", label: "Tüm içerikleri düzenle" },
      { code: "Content.EditOwn", label: "Kendi içeriklerini düzenle" },
      { code: "Content.Publish", label: "İçerik yayımla / onayla" },
      { code: "Content.Delete", label: "İçerik sil" },
      { code: "Content.Submission.View", label: "Kullanıcı içerik başvurularını görüntüle" },
      { code: "Content_View", label: "Legacy içerik görüntüleme" },
      { code: "Content_Add", label: "Legacy içerik ekleme" },
      { code: "Content_Edit", label: "Legacy içerik düzenleme" },
      { code: "Content_Approve", label: "Legacy içerik onaylama" },
      { code: "Content_Delete", label: "Legacy içerik silme" },
    ],
  },
  {
    title: "Yorum İşlemleri",
    permissions: [
      { code: "Comments.Moderate", label: "Yorumları gizle/göster" },
      { code: "Comments.Delete", label: "Yorum sil" },
      { code: "Comment_Delete", label: "Legacy yorum silme" },
    ],
  },
  {
    title: "Kullanıcı Yönetimi",
    permissions: [
      { code: "Users.ViewSensitive", label: "Hassas kullanıcı verilerini görüntüle" },
      { code: "Users.ViewBalances", label: "Z-Token bakiyelerini görüntüle" },
      { code: "Users.ViewProgress", label: "Kullanıcı ilerlemelerini görüntüle" },
      { code: "Users.EditProfile", label: "Kullanıcı profillerini düzenle" },
      { code: "Users.AdjustZToken", label: "Z-Token bakiyesi düzenle" },
      { code: "Users.BanAll", label: "Platform genelinde banla" },
      { code: "Users.BanComments", label: "Yorum yasağı uygula" },
      { code: "Users.AssignRole", label: "Rol ataması yap" },
      { code: "Users.AssignPermission", label: "Özel izin ataması yap" },
      { code: "Users.Export", label: "Kullanıcı verilerini dışa aktar" },
    ],
  },
  {
    title: "Ekonomi & Paketler",
    permissions: [
      { code: "Economy.Settings", label: "Ekonomi ayarlarını düzenle" },
      { code: "Economy.Packages", label: "Paket tanımlarını yönet" },
      { code: "Finance.Ledger.View", label: "Finance ledger ekranını görüntüle" },
      { code: "Finance.Payout.Prepare", label: "Payout taslağı oluştur" },
      { code: "Finance.Payout.Approve", label: "Payout onayla/reddet" },
      { code: "Finance.Payout.Pay", label: "Payout ödendi işaretle" },
      { code: "Finance.Export", label: "Finance raporlarını dışa aktar" },
    ],
  },
  {
    title: "Z-Token Yönetimi",
    permissions: [
      { code: "ZTokens.Manage", label: "Z-Token paketlerini yönet" },
      { code: "ZTokens.Export", label: "Z-Token raporlarını dışa aktar" },
      { code: "ZToken_Adjust", label: "Legacy Z-Token bakiye düzeltme" },
    ],
  },
  {
    title: "Gamification",
    permissions: [{ code: "Gamification.Settings", label: "Gamification ayarlarını düzenle" }],
  },
  {
    title: "Site Ayarları",
    permissions: [{ code: "SiteSettings.Manage", label: "Site marka ayarlarını düzenle" }],
  },
  {
    title: "Bildirimler",
    permissions: [
      { code: "Notifications.SendSingle", label: "Tek kullanıcıya bildirim gönder" },
      { code: "Notifications.Broadcast", label: "Toplu bildirim gönder" },
    ],
  },
  {
    title: "Loglama",
    permissions: [
      { code: "Logs.Export", label: "Log kayıtlarını dışa aktar" },
      { code: "Logs.Manage", label: "Log kayıtlarını yönet" },
    ],
  },
  {
    title: "Sözleşme Yönetimi",
    permissions: [
      { code: "Contracts.View", label: "Sözleşmeleri görüntüle" },
      { code: "Contracts.Manage", label: "Sözleşmeleri yönet" },
      { code: "Contracts.Export", label: "Sözleşmeleri dışa aktar" },
    ],
  },
  {
    title: "Feature Flags",
    permissions: [
      { code: "FeatureFlags.View", label: "Feature flag listesini görüntüle" },
      { code: "FeatureFlags.Manage", label: "Feature flag ayarlarını yönet" },
    ],
  },
  {
    title: "Feedback",
    permissions: [
      { code: "Feedback.View", label: "Feedback kayıtlarını görüntüle" },
      { code: "Feedback.Manage", label: "Feedback kayıtlarını yönet" },
      { code: "Feedback.Export", label: "Feedback kayıtlarını dışa aktar" },
      { code: "Feedback.Comment", label: "Feedback kaydına yorum ekle" },
    ],
  },
  {
    title: "KVKK Talepleri",
    permissions: [
      { code: "Privacy.Requests.View", label: "KVKK taleplerini görüntüle" },
      { code: "Privacy.Requests.Manage", label: "KVKK taleplerini yönet" },
    ],
  },
];

const LABEL_MAP: Record<string, string> = GROUP_DEFINITIONS.reduce((map, group) => {
  group.permissions.forEach((permission) => {
    map[permission.code] = permission.label;
  });
  return map;
}, {} as Record<string, string>);

export function formatPermissionLabel(code: string | null | undefined): string {
  if (!code) {
    return "-";
  }
  return LABEL_MAP[code] ?? code.replace(/_/g, " ");
}

type BuildGroupOptions = {
  filterCodes?: string[] | null;
  hideEmpty?: boolean;
};

function buildFromDefinitions(availableCodes: string[]): PermissionGroupView[] {
  const codeSet = new Set(availableCodes);
  const groups: PermissionGroupView[] = GROUP_DEFINITIONS.map((group) => ({
    title: group.title,
    description: group.description,
    items: group.permissions
      .filter((item) => codeSet.has(item.code))
      .map((item) => ({ code: item.code, label: item.label, hint: item.hint })),
  })).filter((group) => group.items.length > 0);

  const leftovers = availableCodes.filter((code) => !LABEL_MAP[code]);
  if (leftovers.length > 0) {
    groups.push({
      title: "Diğer İzinler",
      items: leftovers
        .sort((a, b) => a.localeCompare(b, "tr-TR"))
        .map((code) => ({ code, label: code })),
    });
  }

  return groups;
}

function buildFromCatalog(groups: PermissionCatalogGroup[], options?: BuildGroupOptions): PermissionGroupView[] {
  const filterSet = options?.filterCodes ? new Set(options.filterCodes) : null;
  const mapped = groups.map((group) => ({
    title: group.title,
    description: group.description ?? undefined,
    items: group.permissions
      .filter((item) => (filterSet ? filterSet.has(item.code) : true))
      .map((item) => ({ code: item.code, label: item.label, hint: item.hint ?? undefined })),
  }));

  return options?.hideEmpty ? mapped.filter((group) => group.items.length > 0) : mapped;
}

function isStringArray(value: string[] | PermissionCatalogGroup[]): value is string[] {
  if (!Array.isArray(value)) {
    return false;
  }

  for (let index = 0; index < value.length; index += 1) {
    if (typeof value[index] !== "string") {
      return false;
    }
  }

  return true;
}

export function buildPermissionGroups(
  source: string[] | PermissionCatalogGroup[],
  options?: BuildGroupOptions,
): PermissionGroupView[] {
  if (source.length === 0) {
    return [];
  }

  if (isStringArray(source)) {
    return buildFromDefinitions(source);
  }

  return buildFromCatalog(source, options);
}

export function createPermissionLabelMap(
  groups?: PermissionCatalogGroup[] | null,
): Record<string, string> {
  if (Array.isArray(groups) && groups.length > 0) {
    const map: Record<string, string> = {};
    groups.forEach((group) => {
      group.permissions.forEach((permission) => {
        map[permission.code] = permission.label;
      });
    });
    if (Object.keys(map).length > 0) {
      return map;
    }
  }

  return { ...LABEL_MAP };
}
